<template>
  <SoAlert
    v-if="loadError"
    title="Failed to load test account health"
    :text="loadError"
    data-testid="test-account-health-error"
  />
  <template v-else>
    <SoAlert
      v-if="rpState === 'loaded'"
      type="info"
      title="ReportPortal test results"
      :text="rpBannerText"
      data-testid="rp-status-banner"
    />
    <SoAlert
      v-else-if="rpState === 'unavailable'"
      type="info"
      title="Live ReportPortal status unavailable"
      text="Statuses could not be fetched (proxy missing or unconfigured), so
        chips link straight to ReportPortal instead."
      data-testid="rp-cors-banner"
    />
    <SoDataTable
      :headers="headers"
      :items="filteredItems"
      :loading="loading"
      :has-pagination="false"
      select-key="account"
      searchable
      search-placeholder="Search account, role, state, or test"
      data-testid="test-account-health-table"
    >
      <template #tableActions>
        <SoSelect
          v-model="verdictFilter"
          :items="['All', 'OK', 'DRIFTED', 'FLAPPING', 'ERROR']"
          label="Verdict"
          type="simple"
          data-testid="health-verdict-filter"
        />
        <SoButton
          title="Re-check"
          data-test-id="health-recheck"
          :disabled="loading"
          @click="refresh"
        />
      </template>
      <template #item.health="{ internalItem }">
        <HealthVerdictChip :health="internalItem.raw.health" />
      </template>
      <template #item.passRate="{ internalItem }">
        <SoTooltip
          :text="passRateTooltip(internalItem.raw.passRate)"
          :disabled="internalItem.raw.passRate === null"
        >
          <span data-testid="rp-pass-rate">
            <SoChip
              v-if="internalItem.raw.passRate !== null"
              :type="passRateChipType(internalItem.raw.passRate)"
              :text="`${internalItem.raw.passRate.pct}%`"
              size="small"
            />
            <template v-else>—</template>
          </span>
        </SoTooltip>
      </template>
      <template #item.account="{ internalItem }">
        <router-link :to="`/customer/${internalItem.raw.account}`">
          {{ internalItem.raw.account }}
        </router-link>
      </template>
      <template #item.tests="{ internalItem }">
        <SoTooltip
          v-for="test in internalItem.raw.tests"
          :key="test.spec"
          :text="rpHistory(test)"
          :disabled="rpHistory(test).length === 0"
        >
          <a
            :href="rpLaunchesUrl(test.rpProject)"
            target="_blank"
            rel="noopener"
            class="mr-1 text-decoration-none"
            data-testid="rp-test-chip"
            :data-rp-status="latestRpStatus(test)"
          >
            <SoChip :type="rpChipType(test)" :text="test.spec" size="small" />
          </a>
        </SoTooltip>
      </template>
      <template #expandableData="rowData">
        <tr
          v-if="
            rowData.driftedFields.length ||
            rowData.flappingFields.length ||
            rowData.errors.length
          "
        >
          <td colspan="100%" class="pa-4">
            <p v-for="field in rowData.driftedFields" :key="`drift-${field}`">
              {{ driftLine(rowData, field) }}
            </p>
            <p v-for="field in rowData.flappingFields" :key="`flap-${field}`">
              {{ flapLine(rowData, field) }}
            </p>
            <p v-if="rowData.errors.length">
              Errors: {{ rowData.errors.join(", ") }}
            </p>
          </td>
        </tr>
      </template>
    </SoDataTable>
  </template>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import SoDataTable from "@/components/dataTable/SoDataTable.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoSelect from "@/components/formControls/SoSelect.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoChip from "@/components/SoChip.vue"
  import SoTooltip from "@/components/SoTooltip.vue"
  import HealthVerdictChip from "./HealthVerdictChip.vue"
  import { TestAccountHealthService } from "@/services/admin/TestAccountHealthService"
  import { RpStatusService } from "@/services/admin/RpStatusService"
  import errorReporter from "@/services/ErrorService/errorReporter"
  import { TEST_ACCOUNT_MAP } from "@/models/admin/testAccountHealth/testAccountMap"
  import {
    AccountHealthRow,
    AccountStateSample,
    ExpectedAccountState,
    MappedTest,
    ObservedAccountState,
  } from "@/models/admin/testAccountHealth/testAccountHealth.types"
  import { rpLaunchesUrl, RpStatusResponse } from "@/api/reportPortal/client"
  import { SoChipType } from "@/types/components/SoChip"
  import {
    computeAccountPassRate,
    AccountPassRate,
  } from "@/models/admin/testAccountHealth/passRate"
  import { passRateSorter } from "@/components/dataTable/sorters"

  export default defineComponent({
    components: {
      SoDataTable,
      SoButton,
      SoSelect,
      SoAlert,
      SoChip,
      SoTooltip,
      HealthVerdictChip,
    },
    data() {
      return {
        rows: [] as AccountHealthRow[],
        loading: false,
        loadError: null as string | null,
        verdictFilter: "All",
        rpStatuses: null as RpStatusResponse | null,
        rpState: "loading" as "loading" | "loaded" | "unavailable",
        headers: [
          { title: "Account", key: "account", sortable: true },
          { title: "Role", key: "role", sortable: false },
          { title: "Health", key: "health", sortable: false },
          {
            title: "Pass rate",
            key: "passRate",
            sortable: true,
            sort: passRateSorter,
          },
          { title: "Meter", key: "meterKind", sortable: true },
          { title: "Status", key: "accountStatus", sortable: true },
          { title: "Agreement", key: "hasActiveAgreement", sortable: true },
          { title: "Renewal", key: "insideRenewalWindow", sortable: true },
          { title: "Latest read", key: "latestReadingDate", sortable: true },
          { title: "Tests", key: "tests", sortable: false },
        ],
      }
    },
    computed: {
      items() {
        return this.rows.map((row) => {
          const latest = row.samples[row.samples.length - 1]?.state
          return {
            account: row.entry.accountNumber,
            role: row.entry.role,
            health: row.health,
            passRate: computeAccountPassRate(
              row.entry.tests.map(
                (test) =>
                  this.rpStatuses?.results?.[test.rpNameFilter]?.executions ??
                  [],
              ),
            ),
            meterKind: latest?.meterKind,
            accountStatus: latest?.accountStatus,
            hasActiveAgreement: latest?.hasActiveAgreement,
            insideRenewalWindow: latest?.insideRenewalWindow,
            latestReadingDate: latest?.latestReadingDate,
            tests: row.entry.tests,
            driftedFields: row.health.driftedFields,
            flappingFields: row.health.flappingFields,
            errors: row.health.errors,
            expected: row.entry.expected,
            samples: row.samples,
            searchText: [
              row.entry.role,
              JSON.stringify(row.entry.expected),
              JSON.stringify(latest),
              row.entry.tests.map((t) => t.spec).join(" "),
            ].join(" "),
          }
        })
      },
      filteredItems() {
        return this.verdictFilter === "All"
          ? this.items
          : this.items.filter((i) => i.health.verdict === this.verdictFilter)
      },
      rpBannerText(): string {
        const asOf = this.rpStatuses
          ? new Date(this.rpStatuses.fetchedAt)
              .toISOString()
              .slice(0, 16)
              .replace("T", " ")
          : ""
        return `Test results from ReportPortal as of ${asOf} — runs are CI-triggered (usually PR previews), so the latest result may reflect a PR branch.`
      },
    },
    async mounted() {
      await this.refresh()
    },
    methods: {
      rpLaunchesUrl,
      async refresh() {
        if (this.loading) return
        this.loadError = null
        this.loading = true
        this.rpState = "loading"
        try {
          const filters = TEST_ACCOUNT_MAP.flatMap((entry) =>
            entry.tests.map((test) => test.rpNameFilter),
          )
          const [rows, rpStatuses] = await Promise.all([
            TestAccountHealthService.probeAll(TEST_ACCOUNT_MAP, 2),
            RpStatusService.getStatuses(filters),
          ])
          this.rows = rows
          this.rpStatuses = rpStatuses
          this.rpState = rpStatuses ? "loaded" : "unavailable"
        } catch (error) {
          errorReporter.report(error)
          this.rpState = "unavailable"
          this.loadError = `Failed to probe test accounts: ${
            error instanceof Error ? error.message : String(error)
          }`
        } finally {
          this.loading = false
        }
      },
      latestRpStatus(test: MappedTest): string {
        return (
          this.rpStatuses?.results?.[test.rpNameFilter]?.executions[0]
            ?.status ?? "UNKNOWN"
        )
      },
      rpChipType(test: MappedTest): SoChipType {
        switch (this.latestRpStatus(test)) {
          case "PASSED":
            return SoChipType.SUCCESS
          case "FAILED":
            return SoChipType.ERROR
          case "SKIPPED":
          case "INTERRUPTED":
            return SoChipType.WARNING
          default:
            return SoChipType.DEFAULT
        }
      },
      rpHistory(test: MappedTest): string {
        const executions =
          this.rpStatuses?.results?.[test.rpNameFilter]?.executions ?? []
        if (executions.length === 0) return ""
        const marks = executions
          .map((execution) =>
            execution.status === "PASSED"
              ? "✓"
              : execution.status === "FAILED"
                ? "✗"
                : "–",
          )
          .join("")
        const latest = new Date(executions[0].startTime)
          .toISOString()
          .slice(0, 16)
          .replace("T", " ")
        return `${marks} — latest ${latest} (newest first)`
      },
      passRateTooltip(rate: AccountPassRate | null): string {
        if (!rate) return ""
        return `${rate.passed}/${rate.counted} executions passed across this account's mapped tests (last 10 runs per test)`
      },
      passRateChipType(rate: AccountPassRate): SoChipType {
        if (rate.pct === 100) return SoChipType.SUCCESS
        if (rate.pct >= 95) return SoChipType.WARNING
        return SoChipType.ERROR
      },
      driftLine(
        row: { expected: ExpectedAccountState; samples: AccountStateSample[] },
        field: string,
      ): string {
        const expected = row.expected[field as keyof ExpectedAccountState]
        const observed =
          row.samples[row.samples.length - 1]?.state[
            field as keyof ObservedAccountState
          ]
        return Array.isArray(expected)
          ? `${field}: expected one of ${expected.join(
              ", ",
            )} — observed ${observed}`
          : `${field}: expected ${expected} — observed ${observed}`
      },
      flapLine(row: { samples: AccountStateSample[] }, field: string): string {
        const first = row.samples[0]?.state[field as keyof ObservedAccountState]
        const last =
          row.samples[row.samples.length - 1]?.state[
            field as keyof ObservedAccountState
          ]
        return `${field}: sample 1 = ${first}, sample 2 = ${last}`
      },
    },
  })
</script>
